/**
 * SpeyBooks Developer Shell v3.0
 *
 * Interactive API testing terminal docked at the bottom of the content area.
 * Always in document flow — never uses fixed positioning.
 *
 * Collapsed: slim bar with prompt and expand trigger (like Stripe's Developers bar)
 * Expanded: full terminal panel with resize handle
 *
 * Flexbox in Layout shrinks the scrollable <main> when the shell expands.
 * The shell only occupies the content column width — never overlaps the sidebar.
 *
 * v3.0 — Dynamic route data from build-time codegen (shell-routes.ts)
 *         All routes, ID prefixes, and autocomplete derived from the OpenAPI IR.
 *         No hardcoded route data in this file.
 *
 * Features:
 * - Tab autocomplete for paths, methods, and commands
 * - Ghost text suggestions (accept with →)
 * - Clickable IDs in responses (cont_1, inv_29, dir_5, etc.)
 * - Copy button on each response
 * - Persistent command history and panel height (localStorage)
 * - Coloured method input
 * - Command history navigation (↑/↓)
 * - Resizable panel (drag top edge)
 * - `routes` command with category grouping
 *
 * Usage: Add <DevShell /> inside a flex-col container
 * Shortcut: Ctrl+J
 *
 * Styling note: The shell is intentionally always-dark (terminal aesthetic).
 * Raw gray/slate classes are used here as fixed colours, consistent with
 * the SpeyBooks styling skill exception for non-theme-dependent elements.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  KNOWN_PATHS,
  SHELL_CATEGORIES,
  ID_PREFIX_TO_RESOURCE,
  ID_REGEX,
  GHOST_SUGGESTIONS,
  ROUTE_COUNT,
} from '../generated/shell-routes';

// ============================================================================
// Types
// ============================================================================

interface ShellEntry {
  id: string;
  type: 'input' | 'output' | 'error' | 'info' | 'status';
  content: string;
  html?: boolean;
  timestamp: Date;
  copyText?: string;
}

interface LastRequest {
  method: string;
  path: string;
  body?: string;
  status?: number;
  timing?: number;
}

// ============================================================================
// Constants
// ============================================================================

let entryCounter = 0;
const nextId = () => `entry-${++entryCounter}-${Date.now()}`;

const HISTORY_KEY = 'speybooks_shell_history';
const SHELL_HEIGHT_KEY = 'speybooks_shell_height';
const MAX_HISTORY = 100;
const DEFAULT_HEIGHT = 384;
const MIN_HEIGHT = 200;

const BUILTIN_COMMANDS = ['help', 'routes', 'clear', 'history', 'curl', 'env'];
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// ============================================================================
// Utilities
// ============================================================================

function highlightJson(str: string): string {
  return str.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'text-amber-400';
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? 'text-sky-300' : 'text-content-secondary';
      } else if (/true|false/.test(match)) {
        cls = 'text-purple-400';
      } else if (/null/.test(match)) {
        cls = 'text-gray-500';
      }
      return `<span class="${cls}">${match}</span>`;
    }
  );
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function makeIdsClickable(html: string): string {
  // Reset the global regex before each use
  ID_REGEX.lastIndex = 0;
  return html.replace(
    ID_REGEX,
    (match, prefix: string) => {
      const resource = ID_PREFIX_TO_RESOURCE[prefix];
      if (!resource) return match;
      return `<span class="shell-id cursor-pointer underline decoration-dotted decoration-gray-600 hover:decoration-heather-400 hover:text-heather-400 transition-colors" data-id="${match}" data-resource="${resource}">${match}</span>`;
    }
  );
}

function statusColor(code: number): string {
  if (code < 300) return 'text-content-secondary';
  if (code < 500) return 'text-amber-400';
  return 'text-red-400';
}

function methodColor(method: string): string {
  switch (method) {
    case 'GET': return 'text-content-secondary';
    case 'POST': return 'text-sky-400';
    case 'PUT': case 'PATCH': return 'text-amber-400';
    case 'DELETE': return 'text-red-400';
    default: return 'text-gray-400';
  }
}

function methodInputStyle(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET': return 'rgb(var(--color-content-secondary))';
    case 'POST': return '#38bdf8';
    case 'PUT': case 'PATCH': return '#fbbf24';
    case 'DELETE': return '#f87171';
    default: return '#e2e8f0';
  }
}

function loadHistory(): string[] {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
}

function saveHistory(history: string[]) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-MAX_HISTORY))); }
  catch { /* full */ }
}

function loadHeight(): number {
  try {
    const h = parseInt(localStorage.getItem(SHELL_HEIGHT_KEY) || '', 10);
    return h >= MIN_HEIGHT ? h : DEFAULT_HEIGHT;
  } catch { return DEFAULT_HEIGHT; }
}

// ============================================================================
// Autocomplete
// ============================================================================

function getCompletions(input: string): string[] {
  const trimmed = input.trimStart();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();

  // Command or method completion (no space yet)
  if (!trimmed.includes(' ')) {
    const cmds = BUILTIN_COMMANDS.filter(c => c.startsWith(lower) && c !== lower);
    const methods = METHODS
      .filter(m => m.startsWith(trimmed.toUpperCase()) && m !== trimmed.toUpperCase())
      .map(m => m + ' /');
    return [...cmds, ...methods];
  }

  // Path completion
  const parts = trimmed.split(/\s+/);
  const method = parts[0].toUpperCase();
  if (!METHODS.includes(method)) return [];
  const pathPart = parts[1] || '';

  if (!pathPart.startsWith('/')) {
    return KNOWN_PATHS.slice(0, 8).map(p => `${method} ${p}`);
  }

  // Match against known paths; also suggest :id after a resource path
  const matches = KNOWN_PATHS.filter(p => p.startsWith(pathPart) && p !== pathPart);

  // If the user typed a complete base path (e.g. /invoices/) and there's
  // a parameterised variant, suggest it
  if (matches.length === 0 && pathPart.endsWith('/')) {
    const withParam = KNOWN_PATHS.filter(p => p.startsWith(pathPart) || p === `${pathPart}:id`);
    if (withParam.length > 0) {
      return [...new Set(withParam.map(p => `${method} ${p}`))];
    }
  }

  return [...new Set(matches.map(p => `${method} ${p}`))];
}

function getGhostText(input: string, history: string[]): string {
  if (!input.trim()) return '';
  const lower = input.toLowerCase();

  // History match first (most recently used)
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].toLowerCase().startsWith(lower) && history[i] !== input) return history[i];
  }

  // Single completion
  const completions = getCompletions(input);
  if (completions.length === 1) return completions[0];

  // Weighted suggestions from generated data
  for (const cmd of GHOST_SUGGESTIONS) {
    if (cmd.toLowerCase().startsWith(lower) && cmd !== input) return cmd;
  }

  return '';
}

// ============================================================================
// Copy button
// ============================================================================

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="absolute top-1 right-1 px-1.5 py-0.5 text-[10px] font-mono rounded bg-gray-800 hover:bg-gray-700 text-gray-500 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity border border-gray-700"
      title="Copy"
    >
      {copied ? '✓' : 'copy'}
    </button>
  );
}

// ============================================================================
// DevShell Component
// ============================================================================

export default function DevShell() {
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(() => loadHeight());
  const [input, setInput] = useState('');
  const [entries, setEntries] = useState<ShellEntry[]>([
    {
      id: nextId(),
      type: 'info',
      content: `SpeyBooks Shell v3.0  <span class="text-gray-600">(${ROUTE_COUNT} endpoints)</span>\nType <span class="text-heather-400">help</span> for commands. <span class="text-gray-600">Tab</span> autocomplete · <span class="text-gray-600">→</span> accept suggestion · click IDs to fetch.`,
      html: true,
      timestamp: new Date(),
    },
  ]);
  const [cmdHistory, setCmdHistory] = useState<string[]>(() => loadHistory());
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [tabState, setTabState] = useState<{ items: string[]; idx: number }>({ items: [], idx: -1 });
  const lastRequestRef = useRef<LastRequest | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const collapsedInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ghost = useMemo(
    () => isLoading ? '' : getGhostText(input, cmdHistory),
    [input, cmdHistory, isLoading]
  );

  const inputMethod = useMemo(() => {
    const first = input.trimStart().split(/\s/)[0]?.toUpperCase() || '';
    return METHODS.includes(first) ? first : '';
  }, [input]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Ctrl+J toggle
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'j' && e.ctrlKey) {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus on open
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 50);
  }, [isOpen]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [entries]);

  // Persist history & height
  useEffect(() => { saveHistory(cmdHistory); }, [cmdHistory]);
  useEffect(() => {
    try { localStorage.setItem(SHELL_HEIGHT_KEY, String(height)); }
    catch { /* full */ }
  }, [height]);

  // Clickable IDs
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('shell-id')) {
        const id = target.dataset.id;
        const resource = target.dataset.resource;
        if (id && resource) {
          setInput(`GET /${resource}/${id}`);
          inputRef.current?.focus();
        }
      }
    };
    const el = scrollRef.current;
    el?.addEventListener('click', handler);
    return () => el?.removeEventListener('click', handler);
  }, []);

  // ---------------------------------------------------------------------------
  // Entry helpers
  // ---------------------------------------------------------------------------

  const addEntry = useCallback((type: ShellEntry['type'], content: string, html = false, copyText?: string) => {
    setEntries(prev => [...prev, { id: nextId(), type, content, html, copyText, timestamp: new Date() }]);
  }, []);

  // ---------------------------------------------------------------------------
  // Commands
  // ---------------------------------------------------------------------------

  const executeApiCall = useCallback(async (method: string, path: string, body?: string) => {
    const token = localStorage.getItem('speytech_access_token');
    const apiPath = path.startsWith('/api/') ? path : `/api/v1${path.startsWith('/') ? '' : '/'}${path}`;
    lastRequestRef.current = { method, path: apiPath, body };

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) options.body = body;

    const start = performance.now();
    const response = await fetch(apiPath, options);
    const timing = Math.round(performance.now() - start);
    lastRequestRef.current.status = response.status;
    lastRequestRef.current.timing = timing;

    const ct = response.headers.get('content-type') || '';
    const data = ct.includes('application/json') ? await response.json() : await response.text();
    return { status: response.status, data, timing };
  }, []);

  const showHelp = useCallback(() => {
    addEntry('info', [
      '<span class="text-sky-300 font-semibold">COMMANDS</span>',
      '',
      '  <span class="text-content-secondary">GET</span>    /path              Fetch a resource',
      '  <span class="text-sky-400">POST</span>   /path {json}       Create a resource',
      '  <span class="text-amber-400">PUT</span>    /path {json}       Update a resource',
      '  <span class="text-amber-400">PATCH</span>  /path {json}       Partial update',
      '  <span class="text-red-400">DELETE</span> /path              Delete a resource',
      '',
      '  help                      Show this help',
      '  routes                    List API routes',
      '  routes <filter>           Filter routes (e.g. routes invoice)',
      '  clear                     Clear the screen',
      '  history                   Show command history',
      '  curl                      Show last request as curl',
      '  env                       Show environment info',
      '',
      '<span class="text-sky-300 font-semibold">SHORTCUTS</span>',
      '',
      '  <span class="text-gray-300">Tab</span>        Autocomplete (cycle with repeated Tab)',
      '  <span class="text-gray-300">→</span>          Accept ghost suggestion',
      '  <span class="text-gray-300">↑ ↓</span>        Navigate command history',
      '  <span class="text-gray-300">Ctrl+L</span>     Clear screen',
      '  <span class="text-gray-300">Ctrl+C</span>     Cancel input',
      '  <span class="text-gray-300">Ctrl+J</span>     Toggle shell',
      '',
      '<span class="text-sky-300 font-semibold">TIPS</span>',
      '',
      '  Click any <span class="text-heather-400 underline decoration-dotted">ID</span> in a response to load it.',
      '  Hover a response block for the <span class="text-gray-300">copy</span> button.',
      '',
      '<span class="text-gray-500">Paths are relative to /api/v1/</span>',
    ].join('\n'), true);
  }, [addEntry]);

  const showRoutes = useCallback((filter?: string) => {
    const lines: string[] = [];
    let matchCount = 0;

    for (const category of SHELL_CATEGORIES) {
      const filtered = filter
        ? category.routes.filter(r =>
            r.path.toLowerCase().includes(filter) ||
            r.desc.toLowerCase().includes(filter) ||
            r.method.toLowerCase().includes(filter)
          )
        : category.routes;

      if (filtered.length === 0) continue;

      matchCount += filtered.length;
      lines.push('');
      lines.push(`<span class="text-sky-300 font-semibold">${escapeHtml(category.label.toUpperCase())}</span>`);

      for (const r of filtered) {
        const m = r.method.padEnd(7);
        const p = r.path.padEnd(38);
        lines.push(`  <span class="${methodColor(r.method)}">${m}</span> <span class="text-gray-300">${p}</span> <span class="text-gray-600">${escapeHtml(r.desc)}</span>`);
      }
    }

    const header = filter
      ? `<span class="text-sky-300 font-semibold">ROUTES</span> <span class="text-gray-600">matching "${escapeHtml(filter)}"  (${matchCount} of ${ROUTE_COUNT})</span>`
      : `<span class="text-sky-300 font-semibold">ROUTES</span> <span class="text-gray-600">(${ROUTE_COUNT} endpoints)</span>`;

    if (matchCount === 0) {
      addEntry('info', `${header}\n\n  <span class="text-gray-500">No routes match "${escapeHtml(filter ?? '')}"</span>`, true);
    } else {
      addEntry('info', [header, ...lines].join('\n'), true);
    }
  }, [addEntry]);

  const showCurl = useCallback(() => {
    const req = lastRequestRef.current;
    if (!req) { addEntry('error', 'No previous request. Make an API call first.'); return; }
    const parts = [
      `curl -s -X ${req.method} https://api.speybooks.com/v1${req.path.replace('/api/v1', '')}`,
      '  -H "Authorization: Bearer sk_live_YOUR_KEY"',
      '  -H "Content-Type: application/json"',
    ];
    if (req.body) parts.push(`  -d '${req.body}'`);
    parts.push('  | jq');
    const str = parts.join(' \\\n');
    addEntry('info', str, false, str);
  }, [addEntry]);

  const showHistory = useCallback(() => {
    if (cmdHistory.length === 0) { addEntry('info', 'No history yet.'); return; }
    const slice = cmdHistory.slice(-20);
    const lines = slice.map((c, i) =>
      `  <span class="text-gray-500">${String(cmdHistory.length - 20 + i + 1).padStart(3)}</span>  ${escapeHtml(c)}`
    );
    if (cmdHistory.length > 20) lines.unshift(`<span class="text-gray-600">  ... ${cmdHistory.length - 20} older</span>`);
    addEntry('info', lines.join('\n'), true);
  }, [addEntry, cmdHistory]);

  const showEnv = useCallback(() => {
    const token = localStorage.getItem('speytech_access_token');
    addEntry('info', [
      `<span class="text-sky-300">API Base:</span>    /api/v1/`,
      `<span class="text-sky-300">Auth:</span>        ${token ? '<span class="text-content">JWT session active</span>' : '<span class="text-red-400">Not authenticated</span>'}`,
      `<span class="text-sky-300">Endpoints:</span>   ${ROUTE_COUNT}`,
      `<span class="text-sky-300">History:</span>     ${cmdHistory.length} commands`,
    ].join('\n'), true);
  }, [addEntry, cmdHistory]);

  const executeCommand = useCallback(async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    // If collapsed, open the shell first
    if (!isOpen) setIsOpen(true);

    setTabState({ items: [], idx: -1 });
    setCmdHistory(prev => [...prev.filter(c => c !== trimmed), trimmed]);
    setHistoryIdx(-1);
    addEntry('input', trimmed);

    const lower = trimmed.toLowerCase();
    if (lower === 'help') return showHelp();
    if (lower === 'clear') return setEntries([]);
    if (lower === 'history') return showHistory();
    if (lower === 'curl') return showCurl();
    if (lower === 'env') return showEnv();

    // `routes` with optional filter: `routes`, `routes invoice`, `routes GET`
    if (lower === 'routes' || lower.startsWith('routes ')) {
      const filter = trimmed.slice(6).trim().toLowerCase() || undefined;
      return showRoutes(filter);
    }

    const match = trimmed.match(/^(GET|POST|PUT|PATCH|DELETE)\s+(\/\S*)\s*([\s\S]*)?$/i);
    if (!match) {
      addEntry('error', `Unknown command: ${escapeHtml(trimmed)}\nType <span class="text-heather-400">help</span> for available commands.`, true);
      return;
    }

    const method = match[1].toUpperCase();
    const path = match[2];
    const body = match[3]?.trim() || undefined;

    if (body) {
      try { JSON.parse(body); }
      catch { addEntry('error', 'Invalid JSON body. Check your syntax.'); return; }
    }

    setIsLoading(true);
    try {
      const { status, data, timing } = await executeApiCall(method, path, body);
      addEntry('status',
        `<span class="${statusColor(status)}">${status}</span>  <span class="text-gray-600">${timing}ms</span>`,
        true
      );
      const rawJson = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      const escaped = escapeHtml(rawJson);
      const highlighted = typeof data === 'string' ? escaped : highlightJson(escaped);
      const linked = makeIdsClickable(highlighted);
      addEntry('output', linked, true, rawJson);
    } catch (err) {
      addEntry('error', err instanceof Error ? err.message : 'Request failed');
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, addEntry, executeApiCall, showHelp, showRoutes, showHistory, showCurl, showEnv]);

  // ---------------------------------------------------------------------------
  // Key handling (shared between collapsed and expanded input)
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (tabState.items.length > 0) {
        const nextIdx = (tabState.idx + 1) % tabState.items.length;
        setTabState({ items: tabState.items, idx: nextIdx });
        setInput(tabState.items[nextIdx]);
        return;
      }
      const completions = getCompletions(input);
      if (completions.length === 1) {
        setInput(completions[0]);
      } else if (completions.length > 1) {
        setTabState({ items: completions, idx: 0 });
        setInput(completions[0]);
        if (isOpen) addEntry('info', completions.map(c => `  ${c}`).join('\n'));
      }
      return;
    }

    if (tabState.items.length > 0) setTabState({ items: [], idx: -1 });

    if (e.key === 'ArrowRight' && ghost && (e.target as HTMLInputElement).selectionStart === input.length) {
      e.preventDefault();
      setInput(ghost);
      return;
    }

    if (e.key === 'Enter' && !isLoading) {
      executeCommand(input);
      setInput('');
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (cmdHistory.length === 0) return;
      const idx = historyIdx < cmdHistory.length - 1 ? historyIdx + 1 : historyIdx;
      setHistoryIdx(idx);
      setInput(cmdHistory[cmdHistory.length - 1 - idx] || '');
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIdx <= 0) { setHistoryIdx(-1); setInput(''); }
      else {
        const idx = historyIdx - 1;
        setHistoryIdx(idx);
        setInput(cmdHistory[cmdHistory.length - 1 - idx] || '');
      }
      return;
    }

    if (e.key === 'l' && e.ctrlKey) { e.preventDefault(); setEntries([]); return; }
    if (e.key === 'c' && e.ctrlKey) { e.preventDefault(); setInput(''); if (isOpen) addEntry('info', '^C'); return; }
  }, [input, isOpen, isLoading, executeCommand, cmdHistory, historyIdx, addEntry, tabState, ghost]);

  // ---------------------------------------------------------------------------
  // Resize
  // ---------------------------------------------------------------------------

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = height;

    const onMove = (e: MouseEvent) => {
      const maxH = window.innerHeight - 120;
      setHeight(Math.min(Math.max(MIN_HEIGHT, startH + (startY - e.clientY)), maxH));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [height]);

  // ---------------------------------------------------------------------------
  // Render: Collapsed bar
  // ---------------------------------------------------------------------------

  if (!isOpen) {
    return (
      <div className="hidden md:flex items-center shrink-0 h-10 bg-gray-950 border-t border-gray-800">
        {/* Prompt — functional, you can type and hit Enter to auto-expand */}
        <div className="flex items-center gap-2 flex-1 px-4">
          <span className="text-heather-400 font-mono text-sm select-none">$</span>
          <div className="relative flex-1">
            {ghost && !input && (
              <div className="absolute inset-0 flex items-center font-mono text-[13px] pointer-events-none select-none" aria-hidden="true">
                <span className="text-gray-700">Enter a shell command...</span>
              </div>
            )}
            {ghost && input && (
              <div className="absolute inset-0 flex items-center font-mono text-[13px] pointer-events-none select-none" aria-hidden="true">
                <span className="invisible whitespace-pre">{input}</span>
                <span className="text-gray-700">{ghost.slice(input.length)}</span>
              </div>
            )}
            <input
              ref={collapsedInputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter a shell command..."
              className="w-full bg-transparent font-mono text-[13px] text-gray-300 outline-none placeholder:text-gray-700 caret-gray-300"
              style={{ color: inputMethod ? methodInputStyle(inputMethod) : '#e2e8f0' }}
              spellCheck={false}
              autoComplete="off"
            />
          </div>
        </div>

        {/* Right side: label + controls */}
        <div className="flex items-center gap-3 px-4 shrink-0">
          <span className="text-xs font-mono text-gray-500">Shell</span>
          <button
            onClick={() => setIsOpen(true)}
            className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
            title="Expand (Ctrl+J)"
          >
            {/* Chevron up */}
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Expanded panel
  // ---------------------------------------------------------------------------

  return (
    <div
      className="hidden md:flex flex-col shrink-0 bg-gray-950 border-t border-gray-800"
      style={{ height: `${height}px` }}
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        className="h-1 bg-gray-800 cursor-ns-resize hover:bg-heather-600 transition-colors shrink-0"
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900/80 border-b border-gray-800 select-none shrink-0">
        <div className="flex items-center gap-3">
          <svg className="w-3.5 h-3.5 text-heather-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="4 17 10 11 4 5" />
            <line x1="12" y1="19" x2="20" y2="19" />
          </svg>
          <span className="text-xs font-mono font-medium text-gray-400">SpeyBooks Shell</span>
          {isLoading && <span className="text-xs text-amber-400 animate-pulse">running...</span>}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors"
            title="Minimise (Ctrl+J)"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button
            onClick={() => { setIsOpen(false); setEntries([]); }}
            className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors"
            title="Close & clear"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Output */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 font-mono text-[13px] leading-relaxed min-h-0"
        onClick={() => { if (!window.getSelection()?.toString()) inputRef.current?.focus(); }}
      >
        {entries.map(entry => (
          <div key={entry.id} className="mb-1">
            {entry.type === 'input' && (
              <div className="text-gray-500"><span className="text-heather-400">$</span> {entry.content}</div>
            )}
            {entry.type === 'status' && (
              <div className="text-xs mt-0.5 mb-1" dangerouslySetInnerHTML={{ __html: entry.content }} />
            )}
            {entry.type === 'output' && (
              <div className="relative group">
                <pre className="text-gray-300 whitespace-pre-wrap break-words text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: entry.content }} />
                {entry.copyText && <CopyButton text={entry.copyText} />}
              </div>
            )}
            {entry.type === 'error' && (
              entry.html
                ? <div className="text-red-400 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: entry.content }} />
                : <div className="text-red-400 whitespace-pre-wrap">{entry.content}</div>
            )}
            {entry.type === 'info' && (
              <div className="relative group">
                {entry.html
                  ? <pre className="text-gray-400 whitespace-pre-wrap text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: entry.content }} />
                  : <div className="text-gray-400 whitespace-pre-wrap">{entry.content}</div>
                }
                {entry.copyText && <CopyButton text={entry.copyText} />}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="relative flex items-center gap-2 px-4 py-2.5 border-t border-gray-800 bg-gray-900/50 shrink-0">
        <span className="text-heather-400 font-mono text-sm select-none">$</span>
        <div className="relative flex-1">
          {ghost && (
            <div className="absolute inset-0 flex items-center font-mono text-[13px] pointer-events-none select-none" aria-hidden="true">
              <span className="invisible whitespace-pre">{input}</span>
              <span className="text-gray-700">{ghost.slice(input.length)}</span>
            </div>
          )}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isLoading ? 'Running...' : 'Type a command or press Tab'}
            disabled={isLoading}
            className="relative w-full bg-transparent font-mono text-[13px] outline-none placeholder:text-gray-700 disabled:opacity-50 caret-gray-300"
            style={{ color: inputMethod ? methodInputStyle(inputMethod) : '#e2e8f0' }}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );
}
